import { Link } from "react-router-dom";
import { MessageCircle, Mail, Phone, MapPin, Clock, ShieldCheck } from "lucide-react";
import Logo from "./Logo";
import CurrencySelector from "./CurrencySelector";
import { useSite } from "../context/SiteContext";
import { track } from "../lib/api";

export default function Footer() {
  const { settings } = useSite();
  const wa = `https://wa.me/${settings.whatsapp_number}?text=${encodeURIComponent("Hi Built2Last.live — I have a project in mind.")}`;

  return (
    <footer className="bg-navy-900 border-t border-white/10" data-testid="site-footer">
      <div className="container-x py-20 lg:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <Logo />
            <p className="mt-6 text-slate-400 text-sm leading-relaxed max-w-sm">
              BUILD SOFTWARE. LIVE. Software built at the speed of business.
            </p>
            <a
              href={wa}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-whatsapp-cta"
              onClick={() => track("whatsapp_click", { location: "footer" })}
              className="mt-8 inline-flex items-center gap-3 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-6 py-3 text-sm font-semibold text-emerald-400 hover:bg-emerald-500/20 transition-colors duration-200"
            >
              <MessageCircle className="w-4 h-4" /> Have a project in mind? WhatsApp us.
            </a>
            <div className="mt-8">
              <p className="font-mono text-[9px] tracking-[0.25em] text-slate-500 mb-3">CURRENCY</p>
              <CurrencySelector testid="footer-currency-selector" />
            </div>
          </div>

          <div className="md:col-span-2">
            <p className="section-label mb-6">SERVICES</p>
            <ul className="space-y-3 text-sm text-slate-400">
              <li><Link className="hover:text-flame transition-colors" to="/standard" data-testid="footer-link-standard">Standard</Link></li>
              <li><Link className="hover:text-flame transition-colors" to="/rush-hour" data-testid="footer-link-rush-hour">Rush Hour</Link></li>
              <li><Link className="hover:text-flame transition-colors" to="/pricing" data-testid="footer-link-pricing">Pricing</Link></li>
              <li><Link className="hover:text-flame transition-colors" to="/case-studies" data-testid="footer-link-case-studies">Case Studies</Link></li>
              <li><Link className="hover:text-flame transition-colors" to="/live-build" data-testid="footer-link-live-build">Live Build</Link></li>
              <li><Link className="hover:text-flame transition-colors" to="/contact" data-testid="footer-link-contact">Contact</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="section-label mb-6">LEGAL</p>
            <ul className="space-y-3 text-sm text-slate-400">
              <li><Link className="hover:text-flame transition-colors" to="/privacy-policy" data-testid="footer-link-privacy">Privacy Policy</Link></li>
              <li><Link className="hover:text-flame transition-colors" to="/terms-and-conditions" data-testid="footer-link-terms">Terms & Conditions</Link></li>
              <li><Link className="hover:text-flame transition-colors" to="/refund-policy" data-testid="footer-link-refund">Refund Policy</Link></li>
              <li><Link className="hover:text-flame transition-colors" to="/cookie-policy" data-testid="footer-link-cookies">Cookie Policy</Link></li>
              <li><Link className="hover:text-flame transition-colors" to="/legal" data-testid="footer-link-legal">All Legal</Link></li>
              <li><Link className="hover:text-flame transition-colors" to="/admin/login" data-testid="footer-link-admin">Admin Login</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="section-label mb-6">CONTACT</p>
            <ul className="space-y-4 text-sm text-slate-400">
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-flame shrink-0" />
                <a href={`mailto:${settings.contact_email}`} data-testid="footer-email" className="hover:text-flame transition-colors">{settings.contact_email}</a>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-flame shrink-0" />
                <a href={`tel:${settings.phone.replace(/\s/g, "")}`} data-testid="footer-phone" onClick={() => track("phone_click", { location: "footer" })} className="hover:text-flame transition-colors">{settings.phone}</a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-flame shrink-0 mt-0.5" />
                <span data-testid="footer-address">{settings.address}</span>
              </li>
              <li className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-flame shrink-0" />
                <span>{settings.working_hours}</span>
              </li>
            </ul>
            <p className="mt-8 flex items-center gap-2 text-xs text-slate-500">
              <ShieldCheck className="w-4 h-4 text-emerald-400" /> Secure payments powered by Razorpay
            </p>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-xs text-slate-500">© {new Date().getFullYear()} Built2Last.live. All rights reserved.</p>
          <p className="text-xs font-mono text-slate-500 tracking-widest">SOFTWARE BUILT AT THE SPEED OF BUSINESS.</p>
        </div>
      </div>
    </footer>
  );
}
